import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Lottie from "lottie-react";
import searchAnimation from "../../Assets/Loader/search.json";
import { FiEye, FiEyeOff, FiShield } from "react-icons/fi";
import DriftWall from "../../Components/SignIn/DriftWall";
import { APPRoutes } from "../../Utils/Route";
import { checkValidity, updatePassword } from "../../Services/AuthenticationService";
import { toast } from "react-toastify";

// Product screenshots that drift behind the card. Loaded in bulk from
// the pre-compressed Driftwall_images folder so the background stays lightweight.
const picsContext = require.context("../../Assets/Images/Driftwall_images", false, /\.jpe?g$/);
const DRIFT_WALL_ITEMS = picsContext.keys().map((key) => ({ image: picsContext(key) }));

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [entireLoading, setEntireLoading] = useState(true);

  const tokenFromParams = searchParams.get("token") || "";
  const emailFromParams = searchParams.get("email") || "";

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: emailFromParams,
      newPassword: "",
      confirmPassword: "",
      token: tokenFromParams,
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .required("New password is required")
        .min(8, "Password must be at least 8 characters"),
      confirmPassword: Yup.string()
        .required("Confirm password is required")
        .oneOf([Yup.ref("newPassword")], "Passwords must match"),
    }),
    onSubmit: async (values) => {
      try {
        const response = await updatePassword({
          email: values.email,
          token: values.token,
          new_password: values.newPassword,
          confirm_password: values.confirmPassword,
        });
        if (response?.is_success) {
          toast.success("Password updated successfully");
          navigate(APPRoutes.LOGIN);
        } else if (response?.is_success === false && response?.status_code === 500) {
          toast.error("Internal Server Error. Please Contact Support Team.");
        } else {
          toast.error(response?.message || "Failed to update password");
        }
      } catch (error) {
        console.error("Error updating password:", error);
        toast.error("An error occurred while updating the password");
      }
    },
  });

  const validateToken = async () => {
    try {
      setEntireLoading(true);
      const response = await checkValidity(emailFromParams, tokenFromParams);
      if (response?.is_success) {
        setEntireLoading(false);
      } else if (response?.status_code === 500) {
        toast.error("Internal Server Error. Please Contact Support Team.");
        setEntireLoading(false);
      } else {
        if (response?.data?.IsValidity) {
          navigate(APPRoutes.VALIDATION_EXPIRES);
        } else {
          toast.error(response?.data?.Message || "Invalid or expired reset link");
        }
        setEntireLoading(false);
      }
    } catch (error) {
      console.error("Token validation error:", error);
      setEntireLoading(false);
    }
  };

  useEffect(() => {
    if (tokenFromParams && emailFromParams) {
      validateToken();
    } else {
      setEntireLoading(false);
    }
  }, [tokenFromParams, emailFromParams]);

  const getFieldError = (fieldName) =>
    formik.touched[fieldName] && formik.errors[fieldName] ? formik.errors[fieldName] : "";

  const renderLoadingSpinner = () => (
    <div data-bs-theme="light" className="hc-auth-shell position-relative min-vh-100 w-100 overflow-hidden">
      <div className="hc-auth-driftwall-wrap position-absolute">
        <DriftWall items={DRIFT_WALL_ITEMS} fade={0.05} />
      </div>
      <div className="hc-auth-center position-relative min-vh-100 w-100 d-flex align-items-center justify-content-center pe-none">
        <div
          className="hc-auth-panel position-relative w-100 d-flex flex-column align-items-center justify-content-center text-center p-5 pe-auto"
          style={{ maxWidth: "420px" }}
        >
          <Lottie
            animationData={searchAnimation}
            loop={true}
            style={{ width: "4.5rem", height: "4.5rem" }}
          />
          <p className="mt-3 mb-0 fw-semibold fs-6" style={{ color: "var(--hc-heading-dark)" }}>
            Validating reset link...
          </p>
        </div>
      </div>
    </div>
  );

  if (entireLoading) {
    return renderLoadingSpinner();
  }

  return (
    <div data-bs-theme="light" className="hc-auth-shell position-relative min-vh-100 w-100 overflow-hidden">
      <div className="hc-auth-driftwall-wrap position-absolute">
        <DriftWall items={DRIFT_WALL_ITEMS} fade={0.05} />
      </div>

      <div className="hc-auth-center position-relative min-vh-100 w-100 d-flex align-items-center justify-content-center pe-none">
        <div className="hc-auth-panel position-relative w-100 d-flex flex-column justify-content-center pe-auto overflow-hidden">
          <div className="hc-auth-header d-flex flex-column align-items-start">
            <div className="hc-auth-hero-pill align-self-start d-inline-flex align-items-center py-1 px-3 rounded-pill text-nowrap pe-none">
              <FiShield size={14} className="flex-shrink-0" />
              <span>Secure connection</span>
            </div>

            <div className="hc-auth-header-row d-flex align-items-center w-100">
              <img
                src="https://www.aegishs.us/aegis-img/badge_logo_ring.png"
                alt="Aegis 360"
                className="hc-auth-logo object-fit-contain"
              />
              <div className="hc-auth-header-text flex-fill">
                <h2 className="hc-auth-title text-start m-0">
                  Reset <span className="hc-auth-title-accent">Password</span>
                </h2>
                <p className="hc-auth-subtitle text-start mt-1 mx-0 mb-0">
                  Set a new password for your account
                </p>
              </div>
            </div>
          </div>

          <div className="hc-auth-panel-inner">
            <form onSubmit={formik.handleSubmit} noValidate>
              <div className="hc-auth-field">
                <label htmlFor="newPassword" className="hc-auth-field-label d-block fs-6">
                  New Password
                </label>
                <div className={`hc-auth-underline d-flex align-items-center gap-2${getFieldError("newPassword") ? " is-invalid" : ""}`}>
                  <input
                    name="newPassword"
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    className="hc-auth-input border-0 rounded-0 shadow-none fw-bold p-0"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    style={{ letterSpacing: "0.15rem" }}
                    value={formik.values.newPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    aria-invalid={Boolean(getFieldError("newPassword"))}
                  />
                  <button
                    type="button"
                    className="hc-auth-field-icon d-flex align-items-center border-0 p-0"
                    onClick={() => setShowNewPassword((prev) => !prev)}
                    aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                  >
                    {showNewPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>
                <div className="hc-auth-field-error">{getFieldError("newPassword")}</div>
              </div>

              <div className="hc-auth-field">
                <label htmlFor="confirmPassword" className="hc-auth-field-label d-block fs-6">
                  Confirm Password
                </label>
                <div className={`hc-auth-underline d-flex align-items-center gap-2${getFieldError("confirmPassword") ? " is-invalid" : ""}`}>
                  <input
                    name="confirmPassword"
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    className="hc-auth-input border-0 rounded-0 shadow-none fw-bold p-0"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    style={{ letterSpacing: "0.15rem" }}
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    aria-invalid={Boolean(getFieldError("confirmPassword"))}
                  />
                  <button
                    type="button"
                    className="hc-auth-field-icon d-flex align-items-center border-0 p-0"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                  >
                    {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>
                <div className="hc-auth-field-error">{getFieldError("confirmPassword")}</div>
              </div>

              <button
                type="submit"
                className={`btn hc-auth-submit-btn rounded-pill mt-2 w-100 fw-semibold border-0 fs-5 d-flex align-items-center justify-content-center ${formik.isSubmitting ? "p-0" : "py-2"}`}
                disabled={formik.isSubmitting}
                style={{
                  color: "var(--hc-white)",
                  boxShadow: "0 14px 32px rgba(var(--hc-black-rgb), 0.25)",
                  opacity: formik.isSubmitting ? 0.7 : 1,
                  minHeight: "3rem",
                }}
              >
                {formik.isSubmitting ? (
                  <Lottie
                    animationData={searchAnimation}
                    loop={true}
                    style={{ width: "3.5rem", height: "3.5rem" }}
                  />
                ) : (
                  "Reset Password"
                )}
              </button>
            </form>

            <p className="mt-3 mb-0 text-center fs-6 hc-auth-footer-text">
              Remember your password?
              <Link to={APPRoutes.LOGIN} className="ms-1 text-decoration-none text-nowrap hc-auth-footer-link fw-bold">
                Sign In
              </Link>
            </p>

            <p className="hc-auth-trust-line d-flex align-items-center justify-content-center text-nowrap">
              <FiShield size={13} />
              Secured with encryption &middot; HIPAA compliant
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
